import { useState } from 'react';
import ArticleLink from '@/components/feature/ArticleLink';
import ScrollReveal from '@/components/base/ScrollReveal';
import SectionHeading from '@/components/base/SectionHeading';
import { preTradeChecklist } from '@/mocks/forexTrading';

export default function PreTradeChecklist() {
  const [checked, setChecked] = useState<number[]>([]);
  const total = preTradeChecklist.length;
  const done = checked.length;
  const progress = Math.round((done / total) * 100);
  const isComplete = done === total;

  const toggle = (index: number) => {
    setChecked((current) =>
      current.includes(index) ? current.filter((item) => item !== index) : [...current, index],
    );
  };

  return (
    <section className="bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <SectionHeading
          eyebrow="เช็กลิสต์"
          title="ติ๊กให้ครบก่อนกดปุ่ม Buy หรือ Sell"
          subtitle="ถ้ามีข้อไหนยังตอบไม่ได้ ให้ถือว่ายังไม่พร้อมเปิดออเดอร์ ดีกว่ากดแล้วมานั่งลุ้นทีหลัง"
        />

        <div className="mt-9 grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
          <ScrollReveal>
            <div className="flex h-full flex-col rounded-xl border border-background-200 bg-background-100/60 p-5 md:p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground-600">ความพร้อมของไม้นี้</p>

              <div className="mt-4 flex items-end gap-2">
                <span className="font-num text-5xl font-semibold leading-none text-foreground-950">{done}</span>
                <span className="font-num pb-1.5 text-sm text-foreground-500">/ {total} ข้อ</span>
              </div>

              <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    isComplete ? 'bg-primary-500' : 'bg-accent-500'
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="font-num mt-2 text-xs text-foreground-500">{progress}% พร้อมเปิดออเดอร์</p>

              <div
                className={`mt-4 flex items-start gap-2 rounded-lg border px-3.5 py-3 ${
                  isComplete
                    ? 'border-primary-300 bg-primary-50 text-primary-800'
                    : 'border-background-300 bg-background-50 text-foreground-700'
                }`}
              >
                <i
                  className={`${
                    isComplete ? 'ri-checkbox-circle-line text-primary-700' : 'ri-information-line text-foreground-500'
                  } mt-0.5 text-base`}
                />
                <p className="text-xs leading-relaxed">
                  {isComplete
                    ? 'ครบทุกข้อแล้ว ถ้าตัวเลขในหน้าต่างคำสั่งตรงกับแผน ก็ยืนยันออเดอร์ได้เลย'
                    : `ยังเหลืออีก ${total - done} ข้อ ห้ามกดออเดอร์จนกว่าจะตอบครบ`}
                </p>
              </div>

              <div className="mt-auto flex flex-col gap-3 pt-5">
                <button
                  type="button"
                  onClick={() => setChecked([])}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-refresh-line text-base" />
                  เริ่มเช็กใหม่
                </button>
                <ArticleLink
                  to="/risk-management"
                  className="inline-flex w-fit items-center gap-1.5 text-xs font-semibold text-primary-700 transition-colors hover:text-primary-900"
                >
                  อ่านต่อ: การบริหารความเสี่ยง
                  <i className="ri-arrow-right-line text-sm" />
                </ArticleLink>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={90}>
            <ul className="flex h-full flex-col gap-2.5">
              {preTradeChecklist.map((item, index) => {
                const isChecked = checked.includes(index);
                return (
                  <li key={item}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isChecked}
                      onClick={() => toggle(index)}
                      className={`flex w-full cursor-pointer items-center gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors ${
                        isChecked
                          ? 'border-primary-300 bg-primary-50'
                          : 'border-background-200 bg-background-100/50 hover:border-background-300 hover:bg-background-100'
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border transition-colors ${
                          isChecked
                            ? 'border-primary-500 bg-primary-500 text-background-50'
                            : 'border-background-400 bg-background-50 text-transparent'
                        }`}
                      >
                        <i className="ri-check-line text-xs" />
                      </span>
                      <span className="font-num w-6 shrink-0 text-xs text-foreground-400">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span
                        className={`min-w-0 flex-1 text-sm leading-snug ${
                          isChecked ? 'text-primary-900' : 'text-foreground-800'
                        }`}
                      >
                        {item}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}